import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Lock, Clock, ShieldX, XCircle } from 'lucide-react';

export type ShopBlockReason = 'verification' | 'entitlement' | 'disabled';

interface FeatureBlockedBannerProps {
  /** Feature name to display */
  featureName: string;
  /** Description of what user can do while blocked */
  allowedAction?: string;
  /** Why the feature is blocked – controls copy & icon */
  reason?: ShopBlockReason;
  /** Override title from ShopGate */
  customTitle?: string;
  /** Override badge from ShopGate */
  customBadge?: string;
  /** Override description from ShopGate */
  customDescription?: string;
  /** Optional CTA button */
  ctaLabel?: string;
  /** Called when CTA is clicked */
  onCtaClick?: () => void;
  /** Whether CTA is in loading state */
  ctaLoading?: boolean;
}

const REASON_CONFIG: Record<ShopBlockReason, {
  badge: string;
  icon: typeof Clock;
  title: (name: string) => string;
  description: (name: string, action: string) => string;
}> = {
  verification: {
    badge: 'Väntar på verifiering',
    icon: Clock,
    title: (name) => `${name} är låst`,
    description: (_name, action) =>
      `Din gård måste vara verifierad innan du kan använda denna funktion. Du kan ${action} medan du väntar på godkännande.`,
  },
  entitlement: {
    badge: 'Inte aktiverad',
    icon: XCircle,
    title: (name) => `${name} är inte tillgänglig`,
    description: () =>
      'Gårdsbutiken ingår inte i din plan eller är inte aktiverad. Kontakta Farmly för mer information.',
  },
  disabled: {
    badge: 'Dold',
    icon: ShieldX,
    title: (name) => `${name} är dold`,
    description: () =>
      'Butiken är dold för besökare. Aktivera den för att visa din gårdsbutik publikt.',
  },
};

/**
 * Banner shown when a feature is blocked due to pending verification,
 * missing entitlement, or being disabled.
 * Supports custom copy overrides from ShopGate + optional CTA button.
 */
export function FeatureBlockedBanner({
  featureName,
  allowedAction = 'förbereda innehåll som utkast',
  reason = 'verification',
  customTitle,
  customBadge,
  customDescription,
  ctaLabel,
  onCtaClick,
  ctaLoading,
}: FeatureBlockedBannerProps) {
  const config = REASON_CONFIG[reason];
  const Icon = config.icon;

  const title = customTitle ?? config.title(featureName);
  const badge = customBadge ?? config.badge;
  const description = customDescription ?? config.description(featureName, allowedAction);

  return (
    <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 mb-6">
      <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      <AlertTitle className="text-amber-800 dark:text-amber-200 flex items-center gap-2">
        {title}
        <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200">
          <Icon className="h-3 w-3 mr-1" />
          {badge}
        </Badge>
      </AlertTitle>
      <AlertDescription className="text-amber-700 dark:text-amber-300 mt-2">
        <p>{description}</p>
        {ctaLabel && onCtaClick && (
          <Button
            size="sm"
            className="mt-3"
            onClick={onCtaClick}
            disabled={ctaLoading}
          >
            {ctaLoading ? 'Aktiverar…' : ctaLabel}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
